import type { PbjCategoryScore } from "../../../utils/models";
import { computeCumulativePass, DAYS_PER_WEEK_FULL, DAYS_PER_WEEK_WEEKEND } from "./cumulativeScoring.utils";
import type { PlanItem } from "./recommendations.constants";
import { RECOMMENDATION_TYPES } from "./recommendations.constants";

// Recommendation ids that only land on weekend days. Each item's `spend` is
// per-applicable-PPD ($/weekend-PPD for weekend recs, $/calendar-PPD otherwise), so its
// quarter-total dollar cost multiplies by the matching day count. Shared with ActionItems.
export const WEEKEND_REC_IDS = new Set(RECOMMENDATION_TYPES.filter((r) => r.isWeekend).map((r) => r.id));

// Every input the plan-outcome math needs — the same values ActionItems already receives.
export type PlanOutcomeInput = {
	selectedPlan: PlanItem[];
	// HPPD categories (band ladders + rawValue baselines) the cumulative pass scores against.
	categories: PbjCategoryScore[];
	currentPoints: number;
	// Buffered target points (target × (1 + buffer%)); compared raw so the FE never disagrees
	// with the BE's `targetMet`.
	targetPoints: number;
	census: number;
	quarterDays: number;
	quarterWeekendDays: number;
	// Remaining calendar days (0 when the quarter is past). Switches the displayed PPD onto a
	// catch-up basis in "qtd" mode. The dollar cost is mode-invariant.
	daysRemaining: number;
	periodMode: "qtd" | "quarter";
};

// The single lifted plan outcome — computed once in ScoringAndRecommendations and shared by
// the hero (verdict + gauge secondary arc) and ActionItems (summary). Every points figure
// comes from the cumulative pass so per-item, per-category, and total never disagree.
export type PlanOutcome = {
	// True when the plan has at least one action (a non-empty Action Plan).
	hasPlan: boolean;
	// Total marginal points the plan adds (cumulative pass — order/split invariant).
	pointsGained: number;
	// current + pointsGained, as a raw float (round only for display).
	projectedPoints: number;
	// Points still short of target after the plan (0 when the target is reached), ceil-rounded.
	remainingPoints: number;
	// projectedPoints >= targetPoints (compared raw, matching the BE `targetMet`).
	reachesTarget: boolean;
	// Total PPD Spend across the plan, on the current period mode's effective window.
	totalPpd: number;
	// Total projected dollar cost (mode-invariant: same lift = same dollars).
	totalPrice: number;
	// Headline weekly hours across the plan — for the hero's one-line "+{hours} hrs/wk". Sums
	// each item's per-week hours (per-applicable-day rate × days/week: weekend items × 2, else × 7).
	headlineWeeklyHours: number;
};

// Per-item projected dollar cost — `item.spend × census × applicableQuarterDays`. Weekend
// items use the weekend-day count because their hours only land on weekend shifts. Mirrors
// ActionItems' `itemPrice` exactly so the hero verdict and the plan summary agree to the cent.
const itemPrice = (item: PlanItem, input: PlanOutcomeInput): number => {
	const days = WEEKEND_REC_IDS.has(item.id) ? input.quarterWeekendDays : input.quarterDays;
	return item.spend * input.census * days;
};

/**
 * Lifts the Action-Plan outcome (projected score, cost, reaches-target, headline hours) out
 * of ActionItems so the hero can render the same numbers. Pure — no hooks, no rounding beyond
 * what's documented per field. Empty plan → `hasPlan:false` with a `projectedPoints === current`
 * baseline so the hero can show a "suggest a plan" prompt without special-casing NaNs.
 */
export const computePlanOutcome = (input: PlanOutcomeInput): PlanOutcome => {
	const { selectedPlan, categories, currentPoints, targetPoints, census, quarterDays, daysRemaining, periodMode } = input;

	const cumulative = computeCumulativePass(selectedPlan, categories);
	const pointsGained = cumulative.total;
	const projectedPoints = currentPoints + pointsGained;
	// Raw-float hit math so the UI never disagrees with the BE's `targetMet`.
	const remainingPoints = Math.max(0, Math.ceil(targetPoints - projectedPoints));
	const reachesTarget = remainingPoints === 0;

	const totalPrice = selectedPlan.reduce((sum, item) => sum + itemPrice(item, input), 0);
	// Effective window the displayed PPD averages over: quarter mode = full quarter; qtd mode =
	// remaining catch-up days only (so the per-day rate scales up). Guards a completed quarter.
	const effDays = periodMode === "qtd" && daysRemaining > 0 ? daysRemaining : quarterDays;
	const totalPpd = census > 0 && effDays > 0 ? totalPrice / (census * effDays) : 0;

	// Headline weekly hours: each item's per-applicable-day rate → per-week (weekend × 2, else × 7).
	const headlineWeeklyHours = selectedPlan.reduce(
		(sum, item) => sum + item.hours * (WEEKEND_REC_IDS.has(item.id) ? DAYS_PER_WEEK_WEEKEND : DAYS_PER_WEEK_FULL),
		0,
	);

	return {
		hasPlan: selectedPlan.length > 0,
		pointsGained,
		projectedPoints,
		remainingPoints,
		reachesTarget,
		totalPpd,
		totalPrice,
		headlineWeeklyHours,
	};
};
